import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const BMICalculatorPage = () => {
  const [unitSystem, setUnitSystem] = useState('us');
  const [age, setAge] = useState('25');
  const [gender, setGender] = useState('male');
  const [heightFeet, setHeightFeet] = useState('5');
  const [heightInches, setHeightInches] = useState('10');
  const [heightCm, setHeightCm] = useState('178');
  const [weightLbs, setWeightLbs] = useState('160');
  const [weightKg, setWeightKg] = useState('73');
  const [result, setResult] = useState(null);

  const bmiCategories = [
    { name: 'Severe Thinness', min: 0, max: 16, color: '#ef4444' },
    { name: 'Moderate Thinness', min: 16, max: 17, color: '#f97316' },
    { name: 'Mild Thinness', min: 17, max: 18.5, color: '#eab308' },
    { name: 'Normal', min: 18.5, max: 25, color: '#22c55e' },
    { name: 'Overweight', min: 25, max: 30, color: '#eab308' },
    { name: 'Obese Class I', min: 30, max: 35, color: '#f97316' },
    { name: 'Obese Class II', min: 35, max: 40, color: '#ef4444' },
    { name: 'Obese Class III', min: 40, max: 100, color: '#dc2626' }
  ];

  const calculate = () => {
    let heightM, weightKgVal;

    if (unitSystem === 'us') {
      const totalInches = parseFloat(heightFeet) * 12 + parseFloat(heightInches);
      heightM = totalInches * 0.0254;
      weightKgVal = parseFloat(weightLbs) * 0.453592;
    } else {
      heightM = parseFloat(heightCm) / 100;
      weightKgVal = parseFloat(weightKg);
    }

    if (heightM > 0 && weightKgVal > 0) {
      const bmi = weightKgVal / (heightM * heightM);
      const category = bmiCategories.find(c => bmi >= c.min && bmi < c.max) || bmiCategories[bmiCategories.length - 1];
      
      const healthyWeightMin = 18.5 * heightM * heightM;
      const healthyWeightMax = 25 * heightM * heightM;
      
      const bmiPrime = bmi / 25;
      const ponderalIndex = weightKgVal / Math.pow(heightM, 3);

      setResult({
        bmi: bmi.toFixed(1),
        category: category.name,
        color: category.color,
        healthyWeightMin: unitSystem === 'us' ? (healthyWeightMin * 2.20462).toFixed(1) : healthyWeightMin.toFixed(1),
        healthyWeightMax: unitSystem === 'us' ? (healthyWeightMax * 2.20462).toFixed(1) : healthyWeightMax.toFixed(1),
        weightUnit: unitSystem === 'us' ? 'lbs' : 'kg',
        bmiPrime: bmiPrime.toFixed(2),
        ponderalIndex: ponderalIndex.toFixed(1)
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/fitness-health" className="text-cyan-400 hover:text-cyan-300">Fitness & Health</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">BMI Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-6">BMI Calculator</h1>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button onClick={() => setUnitSystem('us')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${unitSystem === 'us' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`}>
                US Units
              </button>
              <button onClick={() => setUnitSystem('metric')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${unitSystem === 'metric' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`}>
                Metric Units
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Age</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={age} onChange={(e) => setAge(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" min="2" max="120" />
                  <span className="text-gray-400 text-sm">ages: 2 - 120</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Gender</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="gender" checked={gender === 'male'} onChange={() => setGender('male')}
                      className="w-4 h-4 accent-cyan-500" />
                    <span className="text-gray-300">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="gender" checked={gender === 'female'} onChange={() => setGender('female')}
                      className="w-4 h-4 accent-cyan-500" />
                    <span className="text-gray-300">Female</span>
                  </label>
                </div>
              </div>

              {unitSystem === 'us' ? (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Height</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">feet</span>
                      <Input type="number" value={heightInches} onChange={(e) => setHeightInches(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">inches</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Weight</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">pounds</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Height</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">cm</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="text-gray-300 font-medium w-20">Weight</label>
                    <div className="flex items-center gap-2">
                      <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" />
                      <span className="text-gray-400">kg</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Result</h3>
                </div>

                <div className="text-center mb-6">
                  <p className="text-3xl font-bold text-white mb-2">
                    BMI = <span className="text-cyan-400">{result.bmi}</span> kg/m²
                  </p>
                  <p className="text-xl" style={{ color: result.color }}>({result.category})</p>
                </div>

                <div className="mb-6">
                  <div className="h-4 rounded-full overflow-hidden flex">
                    {bmiCategories.map((cat, i) => (
                      <div key={i} className="h-full" style={{ 
                        backgroundColor: cat.color, 
                        width: `${(cat.max - cat.min) / 50 * 100}%` 
                      }}></div>
                    ))}
                  </div>
                  <div className="relative mt-2" style={{ left: `${Math.min(result.bmi / 50 * 100, 98)}%` }}>
                    <div className="w-0.5 h-4 bg-white mx-auto"></div>
                    <p className="text-xs text-gray-400 text-center">BMI = {result.bmi}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400">Healthy BMI range</p>
                    <p className="text-white font-semibold">18.5 - 25 kg/m²</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400">Healthy weight range</p>
                    <p className="text-white font-semibold">{result.healthyWeightMin} - {result.healthyWeightMax} {result.weightUnit}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400">BMI Prime</p>
                    <p className="text-white font-semibold">{result.bmiPrime}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400">Ponderal Index</p>
                    <p className="text-white font-semibold">{result.ponderalIndex} kg/m³</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/calorie-calculator" className="text-cyan-400 hover:text-cyan-300">Calorie Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/body-fat-calculator" className="text-cyan-400 hover:text-cyan-300">Body Fat Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/ideal-weight-calculator" className="text-cyan-400 hover:text-cyan-300">Ideal Weight</Link>
          </div>

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">BMI Introduction</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                BMI is a measurement of a person's leanness or corpulence based on their height and weight, and is intended 
                to quantify tissue mass. It is widely used as a general indicator of whether a person has a healthy body 
                weight for their height.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">BMI Table for Adults</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2a2a2a]">
                      <th className="text-left py-2 text-gray-400">Classification</th>
                      <th className="text-right py-2 text-gray-400">BMI Range (kg/m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bmiCategories.map((cat, i) => (
                      <tr key={i} className="border-b border-[#2a2a2a]/50">
                        <td className="py-2" style={{ color: cat.color }}>{cat.name}</td>
                        <td className="py-2 text-right text-gray-300">
                          {cat.min === 0 ? `< ${cat.max}` : cat.max === 100 ? `> ${cat.min}` : `${cat.min} - ${cat.max}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Limitations of BMI</h2>
              <p className="text-gray-400 leading-relaxed">
                Although BMI is a widely used and useful indicator of healthy body weight, it does have its limitations. 
                BMI is only an estimate that cannot take body composition into account. Due to a wide variety of body types 
                as well as distribution of muscle, bone mass, and fat, BMI should be considered along with other measurements 
                rather than being used as the sole method for determining a person's healthy body weight.
              </p>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'BMI', path: '/bmi-calculator' },
                { name: 'Calorie', path: '/calorie-calculator' },
                { name: 'Body Fat', path: '/body-fat-calculator' },
                { name: 'BMR', path: '/bmr-calculator' },
                { name: 'Ideal Weight', path: '/ideal-weight-calculator' },
                { name: 'Pace', path: '/pace-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>

          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BMICalculatorPage;
